import { useQuery } from "@tanstack/solid-query";
import { axiosInstance } from "@/api/client";
import { useCompanyStore } from "@/domains/company/company/store/company-store";

/**
 * The selected workspace id, or `undefined` outside the company provider.
 *
 * `useCompanyStore` throws when the context is missing — correct for surfaces
 * that genuinely need a workspace, wrong here: a paywall affordance must never
 * be the thing that brings a screen down. Without a workspace the query simply
 * stays disabled and the action renders inert.
 */
const useSelectedCompanyId = (): (() => string | undefined) => {
  try {
    const [store] = useCompanyStore();
    return () => store.selectedCompany?.id;
  } catch {
    return () => undefined;
  }
};

/** What the caller's plan unlocks in the anonymization domain. */
export type AnonymizationCapabilities = {
  /** Anonymous branded CV — active search and ATS. */
  anonymous_cv: boolean;
  /** AI candidate report PDF — ATS. */
  candidate_report: boolean;
  /** The plan the answer was derived from. */
  plan: string;
};

const ANONYMIZATION_KEY = "anonymization";

// 1. Base fetcher — not gated: it answers for users with no entitlement too,
//    which is the whole point of asking.
export const getAnonymizationCapabilities = async () => {
  const { data } = await axiosInstance.get<AnonymizationCapabilities>(
    "/v1/anonymization/capabilities",
  );
  return data;
};

// 2. Query config — keyed per workspace, so switching company re-fetches: the
//    entitlement follows the workspace owner's plan, not the signed-in profile.
export const createAnonymizationCapabilitiesQueryConfig = (
  companyId: string | undefined,
) => ({
  queryKey: [ANONYMIZATION_KEY, "capabilities", companyId],
  queryFn: () => getAnonymizationCapabilities(),
  staleTime: 5 * 60 * 1000,
});

// 3. Custom hook
export const useAnonymizationCapabilities = () => {
  const selectedCompanyId = useSelectedCompanyId();
  return useQuery(() => {
    const companyId = selectedCompanyId();
    return {
      ...createAnonymizationCapabilitiesQueryConfig(companyId),
      enabled: !!companyId,
    };
  });
};
